import api from '@dashboard/services/axiosApi'
import { isAxiosError } from 'axios'
import { z } from 'zod'
import type { RightyAttachment } from '../types'

/** Mirrors `App\Services\Righty\Attachments\AttachmentInspector`; the server re-checks every file by its contents. */
export const MAX_ATTACHMENTS = 10
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
export const MAX_MESSAGE_ATTACHMENT_BYTES = 20 * 1024 * 1024

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif']
const DOCUMENT_EXTENSIONS = ['pdf', 'txt', 'text', 'md', 'markdown', 'csv', 'json']

export const ATTACHMENT_ACCEPT = [
  ...IMAGE_TYPES,
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  ...[...IMAGE_EXTENSIONS, ...DOCUMENT_EXTENSIONS].map((extension) => `.${extension}`)
].join(',')

export const UNSUPPORTED_ATTACHMENT =
  'Righty can read PNG, JPEG, WebP and GIF images, PDFs, and plain text, Markdown, CSV or JSON files.'

export const RightyAttachmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  mime: z.string(),
  size: z.number(),
  kind: z.enum(['image', 'document']),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  url: z.string().nullable().optional(),
  downloadUrl: z.string().nullable().optional()
})

function extension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

export function isImageFile(file: File): boolean {
  return IMAGE_TYPES.includes(file.type) || IMAGE_EXTENSIONS.includes(extension(file.name))
}

/** A quick client-side check so obvious mistakes fail before uploading; the server decides by contents. */
export function attachmentProblem(file: File): string | null {
  if (!isImageFile(file) && !DOCUMENT_EXTENSIONS.includes(extension(file.name))) {
    return UNSUPPORTED_ATTACHMENT
  }
  if (file.size === 0) {
    return 'This file is empty.'
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `This file is larger than ${formatBytes(MAX_ATTACHMENT_BYTES)}.`
  }
  return null
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** Short file-type label for chips: PDF, CSV, JSON, MD, TXT. */
export function attachmentTypeLabel(attachment: { name: string; mime: string }): string {
  if (attachment.mime === 'application/pdf') {
    return 'PDF'
  }
  if (attachment.mime === 'application/json') {
    return 'JSON'
  }
  if (attachment.mime === 'text/csv') {
    return 'CSV'
  }
  if (attachment.mime === 'text/markdown') {
    return 'MD'
  }
  if (attachment.mime.startsWith('image/')) {
    return 'Image'
  }
  return extension(attachment.name).toUpperCase() || 'File'
}

export async function uploadRightyAttachment(
  teamPid: string,
  file: File,
  options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
): Promise<RightyAttachment> {
  const form = new FormData()
  form.append('file', file, file.name)
  try {
    const { data } = await api.post<unknown>(`/api/${teamPid}/righty/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: options.signal,
      onUploadProgress: (event) => {
        if (event.total) {
          options.onProgress?.(Math.min(1, event.loaded / event.total))
        }
      }
    })
    return z.object({ attachment: RightyAttachmentSchema }).parse(data).attachment
  } catch (error) {
    throw new Error(uploadFailureMessage(error), { cause: error })
  }
}

export function discardRightyAttachment(teamPid: string, id: number): void {
  void api.delete(`/api/${teamPid}/righty/attachments/${id}`).catch(() => undefined)
}

function uploadFailureMessage(error: unknown): string {
  if (isAxiosError<{ message?: unknown }>(error)) {
    const message = error.response?.data?.message
    if (error.response?.status === 429) {
      return 'Too many uploads. Wait a minute and try again.'
    }
    if (error.response?.status === 413) {
      return `This file is larger than ${formatBytes(MAX_ATTACHMENT_BYTES)}.`
    }
    if (typeof message === 'string' && message.trim() && error.response && error.response.status < 500) {
      return message
    }
  }
  return 'Upload failed. Remove it and try again.'
}
